import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * ErrorBoundary global.
 *
 * Sans ce garde-fou, la moindre erreur de rendu démonte tout l'arbre React
 * et laisse une page entièrement vide — qui s'affiche en NOIR sur un appareil
 * en mode sombre (cause des « écrans noirs » signalés par les salariés du
 * coffre-fort). On affiche ici un écran de secours lisible (fond clair) au
 * lieu du vide, avec un bouton pour recharger.
 *
 * Styles volontairement en inline : l'écran de secours ne doit dépendre ni de
 * Tailwind, ni de i18n, ni du routeur — pour rester affichable même si le
 * reste de l'app est cassé.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Au moins visible dans la console tant qu'il n'y a pas de monitoring (Sentry).
    console.error('[ErrorBoundary] Erreur de rendu interceptée:', error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          backgroundColor: '#f1f5f9',
          color: '#0f172a',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: '440px',
            width: '100%',
            textAlign: 'center',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          }}
        >
          <h1 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px' }}>
            Une erreur est survenue
          </h1>
          <p
            style={{
              fontSize: '14px',
              lineHeight: 1.5,
              color: '#475569',
              margin: '0 0 24px',
            }}
          >
            L'affichage de cette page a rencontré un problème. Vos documents
            restent en sécurité. Vous pouvez recharger la page ; si le problème
            persiste, contactez le support.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }
}
